import { Box, CircularProgress, Skeleton, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { KitButton, KitCard, KitItem, KitList, KitPill, SecH } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import { KitBanner, KitEmpty, KitFormBar, KitKV, KitProgress, KitSeg, KitStepper } from '../common/MobileKitExtras';
import MobileItemSearch from '../common/MobileItemSearch';
import StatusChip from '../common/StatusChip';
import { useAppBarAction, useAppBarSubtitle, useAppBarTitle } from '../layout/AppBarActions';
import { formatKm, orderDeadline, orderStatusView, vehicleLabel } from './orderMobileUtils';

/**
 * Operação da ordem (itens, checklist, pagamento) no shell mobile —
 * `prototipos-mobile/ordens/operacao.html`. Só apresentação: itens, totais, ações e
 * permissões vêm de `pages/service_order/Operator.js`.
 */

const WrenchIcon = protoIcon('wrench');
const PackageIcon = protoIcon('package');
const PlusIcon = protoIcon('plus');
const CheckIcon = protoIcon('check');
const PlayIcon = protoIcon('play');
const CardIcon = protoIcon('card');
const DotsIcon = protoIcon('doth');
const PrintIcon = protoIcon('print');
const EditIcon = protoIcon('edit');
const BanIcon = protoIcon('ban');
const ChecksIcon = protoIcon('checks');
const WalletIcon = protoIcon('wallet');
const TagIcon = protoIcon('tag');
const TrashIcon = protoIcon('trash');
const DownloadIcon = protoIcon('download');
const SendIcon = protoIcon('send');
const InboxIcon = protoIcon('inbox');

const PAYMENT_LABEL = {
    'CRÉDITO': 'Crédito',
    'DÉBITO': 'Débito',
    PIXKEY: 'Pix',
    PIXQRCODE: 'Pix QR Code',
    DINHEIRO: 'Dinheiro',
};

function paymentRow(payment) {
    const base = PAYMENT_LABEL[payment.method] || payment.method;
    if (payment.method === 'CRÉDITO' && payment.NumberInstallments > 1) {
        return { label: `${base} ${payment.NumberInstallments}x`, value: `R$ ${payment.ValueInstallment} × ${payment.NumberInstallments}` };
    }
    if (payment.method === 'DINHEIRO' && payment.CashChange && payment.CashChange !== '0,00') {
        return { label: `${base} (troco R$ ${payment.CashChange})`, value: `R$ ${payment.amount}` };
    }
    return { label: base, value: `R$ ${payment.amount}` };
}

// `.sheet .action-list .item`
const ACTION_ITEM_SX = { border: 0, borderBottom: 0, p: '12px 4px' };

function ActionItem({ icon, tone, title, sub, onClick, disabled }) {
    return (
        <KitItem
            icon={icon}
            tone={tone}
            title={title}
            sub={sub}
            chevron
            sx={{ ...ACTION_ITEM_SX, ...(disabled ? { opacity: 0.5 } : {}) }}
            onClick={() => { if (!disabled) onClick(); }}
        />
    );
}

// Linha de peça (`.item` + `.thumb-sq` + `.stepper`)
function ProductRow({ item, editable, busy, onStep, onOpen }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '13px 14px', minHeight: 60, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
            <Box sx={{ width: 44, height: 44, borderRadius: '12px', backgroundColor: 'background.subtle', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', flexShrink: 0 }}>
                <PackageIcon size={20} />
            </Box>
            <Box
                component={editable ? 'button' : 'div'}
                type={editable ? 'button' : undefined}
                onClick={editable ? onOpen : undefined}
                sx={{ flex: 1, minWidth: 0, textAlign: 'left', border: 0, background: 'none', p: 0, fontFamily: 'inherit', color: 'text.primary', cursor: editable ? 'pointer' : 'default' }}
            >
                <Box sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</Box>
                <Box sx={{ fontSize: 12, color: 'text.disabled', mt: '1px', lineHeight: 1.4 }}>
                    R$ {item.amount_unit}{item.item_discount_type ? ` · desc. ${item.item_discount_type}` : ''}
                </Box>
            </Box>
            {editable ? (
                <KitStepper
                    small
                    value={busy ? '…' : item.quantity}
                    onMinus={() => onStep(-1)}
                    onPlus={() => onStep(1)}
                    minusDisabled={busy}
                    plusDisabled={busy}
                />
            ) : (
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Box sx={{ fontSize: 12, color: 'text.disabled' }}>{item.quantity} un</Box>
                    <Box sx={{ fontWeight: 800, fontSize: 14 }}>R$ {item.amount_total}</Box>
                </Box>
            )}
        </Box>
    );
}

export default function MobileOrderOperator({
    loading,
    orderNumber,
    status,
    details,
    usesVehicle,
    checklistEnabled,
    orderTotal,
    items,
    loadingItems,
    attention,
    finance,
    payments,
    checklist,
    loadingChecklist,
    editable,
    canEdit,
    canFillChecklist,
    canExecute,
    canCancel,
    canBill,
    canSendWhatsapp,
    printing,
    isItemBusy,
    onPrint,
    onEdit,
    onChecklist,
    onExecute,
    onFinish,
    onFinishAndBill,
    finishing,
    onBill,
    onCancel,
    onStep,
    onEditItem,
    onDiscount,
    onRemove,
    onSearch,
    searchResults,
    onAddItem,
    addingItem,
    onChecklistPdf,
    downloadingChecklistPdf,
    onChecklistWhatsapp,
}) {
    const theme = useTheme();
    const [tab, setTab] = useState('i');
    const [actionsOpen, setActionsOpen] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [finOpen, setFinOpen] = useState(false);
    const [itemSheet, setItemSheet] = useState(null);
    const [term, setTerm] = useState('');
    const [picked, setPicked] = useState(null);
    const searchTimer = useRef(null);
    const view = orderStatusView(status);
    const statusKey = String(status || '').trim().toLowerCase();
    const isNotStarted = statusKey === 'não iniciada';
    const isRunning = statusKey === 'em execução' || statusKey === 'iniciada';
    const isFinished = statusKey === 'finalizada';

    useAppBarTitle(!loading && orderNumber ? `OS #${orderNumber}` : '');
    useAppBarSubtitle('Operação');
    useAppBarAction({ label: 'Ações', icon: DotsIcon, onClick: () => setActionsOpen(true), disabled: loading });

    useEffect(() => () => clearTimeout(searchTimer.current), []);

    const handleTerm = (value) => {
        setTerm(value);
        setPicked(null);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => onSearch(value.trim()), 250);
    };

    const closeAdd = () => {
        setAddOpen(false);
        setTerm('');
        setPicked(null);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <Skeleton variant="rounded" height={140} sx={{ borderRadius: '16px' }} />
                <Skeleton variant="rounded" height={48} sx={{ borderRadius: '14px' }} />
                <Skeleton variant="rounded" height={190} sx={{ borderRadius: '16px' }} />
            </Box>
        );
    }

    const services = items.filter((item) => item.type === 'service');
    const products = items.filter((item) => item.type === 'product');
    const vehicleLine = usesVehicle && vehicleLabel(details.vehicle_name)
        ? `${vehicleLabel(details.vehicle_name)}${details.vehicle_plate && details.vehicle_plate !== '-' && details.vehicle_type !== 'bicycle' ? ` · ${details.vehicle_plate}` : ''}`
        : '';
    const deadline = orderDeadline({ delivery_date: details.delivery_date, status });
    const tabs = [
        { value: 'i', label: 'Itens' },
        ...(checklistEnabled ? [{ value: 'c', label: 'Checklist' }] : []),
        { value: 'p', label: 'Pagamento' },
    ];

    const hasBar = editable || (isFinished && canBill);

    const evaluations = checklist?.evaluations || [];

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            {/* `.card.grad` */}
            <KitCard sx={{ backgroundImage: theme.palette.gradient.brand, color: '#fff', border: 0, boxShadow: theme.customShadows.md }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <Box sx={{ minWidth: 0 }}>
                        {vehicleLine ? (
                            <>
                                <Box sx={{ fontSize: 12.5, opacity: 0.85, lineHeight: 1.4 }}>{details.customer_name}</Box>
                                <Box sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.4 }}>{vehicleLine}</Box>
                            </>
                        ) : (
                            <Box sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.4 }}>{details.customer_name || '—'}</Box>
                        )}
                    </Box>
                    <KitPill sx={{ '&::before': { display: 'none' }, flexShrink: 0 }}>{view.label}</KitPill>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', mt: '12px' }}>
                    <Box>
                        <Box sx={{ fontSize: 11.5, opacity: 0.8, lineHeight: 1.4 }}>Total</Box>
                        <Box sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.4 }}>R$ {orderTotal}</Box>
                    </Box>
                    <Box>
                        <Box sx={{ fontSize: 11.5, opacity: 0.8, lineHeight: 1.4 }}>Prazo</Box>
                        <Box sx={{ fontWeight: 700, lineHeight: 1.4 }}>{deadline.label}</Box>
                    </Box>
                    {usesVehicle && (
                        <Box>
                            <Box sx={{ fontSize: 11.5, opacity: 0.8, lineHeight: 1.4 }}>KM</Box>
                            <Box sx={{ fontWeight: 700, lineHeight: 1.4 }}>{formatKm(details.vehicle_kilometer)}</Box>
                        </Box>
                    )}
                </Box>
            </KitCard>

            <Box sx={{ mt: '12px' }}>
                <KitSeg options={tabs} value={tab} onChange={setTab} />
            </Box>

            {tab === 'i' && (
                <Box sx={{ mt: '12px' }}>
                    {attention && (
                        <Box sx={{ mb: '12px' }}>
                            <KitBanner tone="danger">{attention}</KitBanner>
                        </Box>
                    )}

                    {loadingItems ? (
                        <Skeleton variant="rounded" height={150} sx={{ borderRadius: '16px' }} />
                    ) : items.length === 0 ? (
                        <KitEmpty icon={InboxIcon} title="Nenhum item nesta ordem">
                            {editable ? 'Toque em Adicionar item para incluir produtos ou serviços.' : 'Esta ordem não possui itens registrados.'}
                        </KitEmpty>
                    ) : (
                        <>
                            {services.length > 0 && (
                                <>
                                    <SecH first title="Serviços" />
                                    <KitList>
                                        {services.map((item) => (
                                            <KitItem
                                                key={item.id}
                                                icon={WrenchIcon}
                                                title={item.name}
                                                sub={[
                                                    details.technician_name ? `Mecânico: ${details.technician_name}` : 'Serviço',
                                                    Number(item.quantity) > 1 ? `${item.quantity} × R$ ${item.amount_unit}` : null,
                                                ].filter(Boolean).join(' · ')}
                                                end={<Box sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}>R$ {item.amount_total}</Box>}
                                                onClick={editable ? () => setItemSheet(item) : undefined}
                                            />
                                        ))}
                                    </KitList>
                                </>
                            )}
                            {products.length > 0 && (
                                <>
                                    <SecH first={services.length === 0} title="Peças" />
                                    <KitCard sx={{ p: 0, overflow: 'hidden' }}>
                                        {products.map((item) => (
                                            <ProductRow
                                                key={item.id}
                                                item={item}
                                                editable={editable}
                                                busy={isItemBusy(item.id)}
                                                onStep={(delta) => onStep(item.id, delta)}
                                                onOpen={() => setItemSheet(item)}
                                            />
                                        ))}
                                    </KitCard>
                                </>
                            )}
                            <KitCard sx={{ mt: '12px' }}>
                                <KitKV totals label="Subtotal" value={`R$ ${finance.subtotal}`} />
                                <KitKV totals label="Desconto" value={`- R$ ${finance.discount}`} />
                                <KitKV totals big label="Total" value={`R$ ${finance.total}`} />
                            </KitCard>
                        </>
                    )}
                </Box>
            )}

            {tab === 'c' && (
                <Box sx={{ mt: '12px' }}>
                    {loadingChecklist ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
                    ) : checklist ? (
                        <>
                            <KitCard>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                                    <Box component="b">{checklist.checklist_name}</Box>
                                    <KitPill tone="success">{evaluations.length}/{evaluations.length}</KitPill>
                                </Box>
                                <KitProgress value={100} tone="green" sx={{ mt: '8px' }} />
                                <Box sx={{ fontSize: 12, color: 'text.disabled', mt: '8px' }}>
                                    {checklist.mechanic_name ? `Preenchido por ${checklist.mechanic_name}` : ''}
                                    {checklist.completion_date ? `${checklist.mechanic_name ? ' · ' : ''}${new Date(checklist.completion_date).toLocaleDateString('pt-BR')}` : ''}
                                </Box>
                                <Box sx={{ mt: '8px' }}>
                                    {evaluations.map((evaluation, index) => (
                                        <Box key={evaluation.evaluation_id || index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', p: '9px 0', borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Box sx={{ fontSize: 13.5, fontWeight: 600 }}>{evaluation.item_name}</Box>
                                                {evaluation.observation && <Box sx={{ fontSize: 12, color: 'text.disabled', fontStyle: 'italic' }}>{evaluation.observation}</Box>}
                                            </Box>
                                            <StatusChip domain="checklist" value={evaluation.answer_description} />
                                        </Box>
                                    ))}
                                </Box>
                            </KitCard>
                            <Box sx={{ display: 'flex', gap: '8px', mt: '12px' }}>
                                <KitButton variant="soft" onClick={onChecklistPdf} disabled={downloadingChecklistPdf} sx={{ flex: 1 }}>
                                    {downloadingChecklistPdf ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <DownloadIcon size={18} />} Baixar PDF
                                </KitButton>
                                {canSendWhatsapp && (
                                    <KitButton variant="soft" onClick={onChecklistWhatsapp} sx={{ flex: 1 }}>
                                        <SendIcon size={18} /> WhatsApp
                                    </KitButton>
                                )}
                            </Box>
                        </>
                    ) : (
                        <KitEmpty icon={ChecksIcon} title="Nenhum checklist preenchido">
                            Preencha uma inspeção do veículo para este atendimento.
                        </KitEmpty>
                    )}
                    {editable && canFillChecklist && (
                        <KitButton variant="soft" block sx={{ mt: '12px' }} onClick={onChecklist}>
                            <ChecksIcon size={18} /> {checklist ? 'Preencher novamente' : 'Preencher checklist'}
                        </KitButton>
                    )}
                </Box>
            )}

            {tab === 'p' && (
                <Box sx={{ mt: '12px' }}>
                    {payments.length === 0 ? (
                        <KitEmpty icon={WalletIcon} title="Nenhum pagamento">
                            {isFinished ? 'Toque em Faturar para receber.' : 'Finalize a OS para receber.'}
                        </KitEmpty>
                    ) : (
                        <KitCard>
                            {payments.map((payment, index) => {
                                const row = paymentRow(payment);
                                return <KitKV key={index} label={row.label} value={row.value} />;
                            })}
                            <Box>
                                <KitKV totals label="Subtotal" value={`R$ ${finance.subtotal}`} />
                                <KitKV totals label="Desconto" value={`- R$ ${finance.discount}`} />
                                <KitKV totals big label="Total" value={`R$ ${finance.total}`} />
                            </Box>
                        </KitCard>
                    )}
                </Box>
            )}

            {hasBar && (
                <KitFormBar>
                    {editable && (
                        <KitButton onClick={() => setAddOpen(true)}><PlusIcon size={18} /> Adicionar item</KitButton>
                    )}
                    {isNotStarted && canExecute && (
                        <KitButton variant="primary" onClick={onExecute}><PlayIcon size={18} /> Iniciar execução</KitButton>
                    )}
                    {isRunning && (
                        <KitButton sx={{ backgroundColor: 'success.main', color: '#fff', border: 0 }} onClick={() => setFinOpen(true)}><CheckIcon size={18} /> Finalizar</KitButton>
                    )}
                    {isFinished && canBill && (
                        <KitButton variant="primary" onClick={onBill}><CardIcon size={18} /> Faturar ordem</KitButton>
                    )}
                </KitFormBar>
            )}

            {/* Folha "Adicionar item" */}
            <MobileSheet
                open={addOpen}
                onClose={closeAdd}
                title="Adicionar item"
                footer={(
                    <KitButton
                        variant="primary"
                        sx={{ flex: 1 }}
                        disabled={!picked || addingItem}
                        onClick={() => {
                            const item = picked;
                            closeAdd();
                            onAddItem(item);
                        }}
                    >
                        {addingItem ? 'Adicionando…' : 'Adicionar'}
                    </KitButton>
                )}
            >
                <MobileItemSearch term={term} onTermChange={handleTerm} results={searchResults} picked={picked} onPick={setPicked} />
            </MobileSheet>

            {/* Folha "Finalizar OS" */}
            <MobileSheet
                open={finOpen}
                onClose={() => setFinOpen(false)}
                title="Finalizar OS"
                footer={(
                    <>
                        <KitButton
                            sx={{ flex: 1 }}
                            disabled={finishing}
                            onClick={async () => {
                                await onFinish();
                                setFinOpen(false);
                            }}
                        >
                            Só finalizar
                        </KitButton>
                        {canBill && (
                            <KitButton
                                sx={{ flex: 1, backgroundColor: 'success.main', color: '#fff', border: 0 }}
                                disabled={finishing}
                                onClick={async () => {
                                    await onFinishAndBill();
                                    setFinOpen(false);
                                }}
                            >
                                Finalizar e receber
                            </KitButton>
                        )}
                    </>
                )}
            >
                <KitCard sx={{ boxShadow: 'none' }}>
                    <KitKV totals label="Total" value={<b>R$ {orderTotal}</b>} />
                </KitCard>
            </MobileSheet>

            {/* Folha "Ações" (⋯ da barra superior) */}
            <MobileSheet open={actionsOpen} onClose={() => setActionsOpen(false)} title="Ações" actionList>
                <Box>
                    <ActionItem icon={PrintIcon} title="Imprimir ordem" disabled={printing} onClick={() => { setActionsOpen(false); onPrint(); }} />
                    {editable && canFillChecklist && checklistEnabled && (
                        <ActionItem icon={ChecksIcon} title="Preencher checklist" onClick={() => { setActionsOpen(false); onChecklist(); }} />
                    )}
                    {editable && canEdit && (
                        <ActionItem icon={EditIcon} title="Editar dados" onClick={() => { setActionsOpen(false); onEdit(); }} />
                    )}
                    {editable && canCancel && (
                        <ActionItem icon={BanIcon} tone="red" title="Cancelar ordem" onClick={() => { setActionsOpen(false); onCancel(); }} />
                    )}
                </Box>
            </MobileSheet>

            {/* Ações do item (editar / desconto / remover) */}
            <MobileSheet open={Boolean(itemSheet)} onClose={() => setItemSheet(null)} title={itemSheet?.name || 'Item'} actionList>
                <Box>
                    <ActionItem icon={EditIcon} title="Editar item" onClick={() => { const item = itemSheet; setItemSheet(null); onEditItem(item); }} />
                    {itemSheet?.type === 'product' && (
                        <ActionItem icon={TagIcon} title="Desconto no produto" onClick={() => { const item = itemSheet; setItemSheet(null); onDiscount(item); }} />
                    )}
                    <ActionItem icon={TrashIcon} tone="red" title="Remover item" onClick={() => { const item = itemSheet; setItemSheet(null); onRemove(item.id); }} />
                </Box>
            </MobileSheet>
        </Box>
    );
}
